import type { ITalkCallsEvent } from '@zyno-platform/shared';

import { onMounted, onUnmounted, watch, type WatchStopHandle } from 'vue';

import { SSETalkCallsChannel } from '@/modules/talk/sse';
import { SSEService } from '@/shared/services/sse';

export type LiveResyncReason = 'reconnect' | 'interval' | 'visible';

export interface LiveInvalidationOptions {
    /** Map an incoming `talk-calls` event to the key(s) it invalidates; null when it isn't ours. */
    keyFor: (event: ITalkCallsEvent) => string | string[] | null;
    /** Coalesced: called once per burst with every key seen since the last flush. */
    onInvalidate: (keys: ReadonlySet<string>) => void | Promise<void>;
    /** Full re-read of the view: after an SSE reconnect, on the periodic tick, and when the tab becomes visible. */
    onResync: (reason: LiveResyncReason) => void | Promise<void>;
    /** Periodic safety net for notifications that never arrive (TTL expiry, pod loss). Default 30 s; 0 disables. */
    intervalMs?: number;
    /** Burst window for `onInvalidate`. Default 250 ms. */
    coalesceMs?: number;
}

export interface LiveInvalidation {
    /** Subscribe and start the timers. Keys are buffered until `ready()`. */
    start(): void;
    /** The initial REST snapshot has rendered: flush anything that arrived while it was in flight. */
    ready(): void;
    /** Unsubscribe and clear every timer, listener and buffered key. */
    stop(): void;
}

const DEFAULT_INTERVAL_MS = 30_000;
const DEFAULT_COALESCE_MS = 250;

/**
 * The runtime-view sync protocol from the Talk API's live-calls contract, in one place: subscribe to
 * the tenant `talk-calls` channel first, buffer invalidations until the caller's initial snapshot has
 * landed, coalesce bursts, and re-read the whole view after a reconnect, on a timer, and when the tab
 * comes back into view. SSE is a refresh signal, never a ledger — the callbacks fetch REST state.
 *
 * Timers are plain `setTimeout` / `setInterval`, not lodash's, which never fire under the visual
 * harness's frozen clock.
 */
export function createLiveInvalidation(opts: LiveInvalidationOptions): LiveInvalidation {
    const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
    const coalesceMs = opts.coalesceMs ?? DEFAULT_COALESCE_MS;

    let started = false;
    let isReady = false;
    const pending = new Set<string>();
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    let intervalTimer: ReturnType<typeof setInterval> | undefined;
    let stopConnectionWatch: WatchStopHandle | undefined;

    // One resync in flight at a time; a request that lands mid-flight runs once more afterwards,
    // so a burst of reasons collapses to at most one extra read rather than a pile-up. A resync
    // asked for before `ready()` (the stream opening while the initial snapshot is still in flight)
    // is held the same way and runs once the snapshot has landed — never concurrently with it.
    let resyncing = false;
    let resyncAgain: LiveResyncReason | undefined;

    const listener = (event: ITalkCallsEvent) => {
        const keys = opts.keyFor(event);
        if (keys === null) return;
        for (const key of Array.isArray(keys) ? keys : [keys]) pending.add(key);
        if (isReady) scheduleFlush();
    };

    function scheduleFlush() {
        if (flushTimer) return;
        flushTimer = setTimeout(flush, coalesceMs);
    }

    function flush() {
        flushTimer = undefined;
        if (!started || !pending.size) return;
        const keys = new Set(pending);
        pending.clear();
        void opts.onInvalidate(keys);
    }

    async function resync(reason: LiveResyncReason) {
        if (!started) return;
        if (resyncing || !isReady) {
            resyncAgain = reason;
            return;
        }
        resyncing = true;
        try {
            await opts.onResync(reason);
        } finally {
            resyncing = false;
        }
        const again = resyncAgain;
        resyncAgain = undefined;
        if (again && started) void resync(again);
    }

    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') void resync('visible');
    };

    function start() {
        if (started) return;
        started = true;
        SSETalkCallsChannel.subscribe(listener);
        stopConnectionWatch = watch(SSEService.isConnected, (up, wasUp) => {
            if (up && wasUp === false) void resync('reconnect');
        });
        if (intervalMs > 0) {
            intervalTimer = setInterval(() => {
                // A hidden tab re-reads the moment it is shown again instead.
                if (typeof document !== 'undefined' && document.hidden) return;
                void resync('interval');
            }, intervalMs);
        }
        if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibilityChange);
    }

    function ready() {
        if (isReady) return;
        isReady = true;
        if (pending.size) scheduleFlush();
        const queued = resyncAgain;
        resyncAgain = undefined;
        if (queued) void resync(queued);
    }

    function stop() {
        if (!started) return;
        started = false;
        isReady = false;
        SSETalkCallsChannel.unsubscribe(listener);
        stopConnectionWatch?.();
        stopConnectionWatch = undefined;
        if (flushTimer) clearTimeout(flushTimer);
        flushTimer = undefined;
        if (intervalTimer) clearInterval(intervalTimer);
        intervalTimer = undefined;
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibilityChange);
        pending.clear();
        resyncAgain = undefined;
    }

    return { start, ready, stop };
}

/** `createLiveInvalidation` bound to the calling component's lifetime. */
export function useLiveInvalidation(opts: LiveInvalidationOptions): Pick<LiveInvalidation, 'ready'> {
    const invalidation = createLiveInvalidation(opts);
    onMounted(() => invalidation.start());
    onUnmounted(() => invalidation.stop());
    return { ready: () => invalidation.ready() };
}
